import { z } from 'zod';

/**
 * The available endpoints of the Search Service.
 * - `search`: Search for directory items from OpenSearch.
 * - `searchDetail`: Same as `search` but for the professional detail pages.
 * - `aggregate`: Get the aggregations for a search.
 * - `searchnav`: Get search suggestions for keywords and categories.
 * - `searchImage`: Search for library images.
 * - `searchImageAggregate`: Get the aggregations for a library image search.
 */
export type SearchServiceEndpoints =
    | 'search'
    | 'searchDetail'
    | 'aggregate'
    | 'searchnav'
    | 'searchImage'
    | 'searchImageAggregate';

/**
 * The request body schema for the Search Service `searchnav` endpoint.
 */
export interface SearchNavRequest {
    search?: string;
    size?: number;
    /** How many professionals to return in the companies group (Search Service defaults to 5). */
    professionalSize?: number;
    country?: string;
}

/**
 * The available sort values for the Search Service search endpoint.
 */
export type SearchServiceSortOption =
    | 'alphabetical'
    | 'created'
    | 'draft'
    | 'featured'
    | 'highPrice'
    | 'lowPrice'
    | 'new'
    | 'newlyCreated'
    | 'popular'
    | 'trending'
    // | 'unpublished' // We don't use unpublished anymore.
    | 'updated'
    | null;

/**
 * The Zod schema for a filter sort option. This is a superset of the Search
 * Service sort options because some sort options are implemented as filters.
 */
export const FILTER_SORT_OPTION_SCHEMA = z.enum([
    'alphabetical',
    'created',
    'draft',
    'featured',
    'highPrice',
    'lowPrice',
    'new',
    'newlyCreated',
    'popular',
    'trending',
    'updated',
    // Sort options that are actually filters:
    'invalidLink',
    'uncategorised',
]);

/**
 * The available sort options for filters. This can be used to generate a sort
 * value and filters for the Search Service.
 */
export type FilterSortOption = z.infer<typeof FILTER_SORT_OPTION_SCHEMA> | null;

/**
 * The Zod schema for the view projects by filter value.
 */
export const VIEW_PROJECTS_BY_OPTION_SCHEMA = z.enum([
    'all',
    'our-projects',
    'projects-we-tagged-in',
]);

/**
 * The available values for the `viewProjectsBy` filter for the Search Service.
 */
export type SearchServiceViewProjectsByOption = z.infer<
    typeof VIEW_PROJECTS_BY_OPTION_SCHEMA
>;

/**
 * The index to query for the Search Service. Used in the `searchType` request
 * parameter.
 */
export type SearchIndex = 'professional' | 'project' | 'product' | 'blogpost';

/**
 * The page to search for. This affects the visibility filter.
 */
export type VisibilityType = 'search' | 'directory' | 'profile';

/**
 * The request body schema for the Search Service `search` endpoint.
 */
export interface SearchServiceRequest {
    /** The search query. */
    search?: string;
    /** The item fields to return. */
    sourceFilter?: string[];
    /** The index to search. Set as undefined to search all indexes. */
    searchType?: SearchIndex;
    /** The page to search for. This affects the visibility filter. Defaults to 'search'. */
    visibilityType?: VisibilityType;
    /** The filters to apply to the search. */
    filters?: SearchServiceFilters;
    /** The sort option to apply to the search. Set empty to sort by relevance score. */
    sortBy?: SearchServiceSortOption;
    /** The page number. Starts at 0. */
    page?: number;
    /** The maximum number of results to return per page. */
    size?: number;
}

/**
 * The request body schema for the Search Service `aggregate` endpoint.
 */
export interface SearchAggregateRequest {
    /** The search query. */
    search?: string;
    /** The index to search. Set as undefined to search all indexes. */
    searchType?: SearchIndex;
    /** The page to search for. This affects the visibility filter. */
    visibilityType?: VisibilityType;
    /** The filters to apply to the search. */
    filters: SearchServiceFilters;
    /** The aggregations to get from the search. */
    aggregations?: SearchAggregationParams;
}

export interface SearchCompositeAggParams {
    size?: number;
    search?: string;
    order?: 'asc' | 'desc';
    after?: Record<string, unknown>;
}

export interface FacetOptionsAggregationParams {
    facet: string;
    search?: string;
}

/**
 * The aggregration params for the Search Service `aggregate` endpoint.
 */
export interface SearchAggregationParams {
    aggCategories?: SearchCompositeAggParams;
    aggProfessionals?: SearchCompositeAggParams;
    aggProfessionalCategories?: SearchCompositeAggParams;
    aggRegions?: SearchCompositeAggParams;
    aggProductPriceRange?: boolean;
    aggProductItemsCount?: boolean;
    aggProductColors?: boolean;
    aggProductMaterials?: SearchCompositeAggParams;
    aggProductLengthRange?: boolean;
    aggProductWidthRange?: boolean;
    aggProductHeightRange?: boolean;
    aggProductDiameterRange?: boolean;
    aggProductWeightRange?: boolean;
    aggBrands?: SearchCompositeAggParams;
    aggDesigners?: SearchCompositeAggParams;
    aggProjectItemsCount?: boolean;
    aggProfessions?: SearchCompositeAggParams;
    aggProfessionalProjectCountRanges?: boolean;
    aggLibraryRanges?: SearchCompositeAggParams;
    aggBusinessFocuses?: boolean;
    aggAssociations?: SearchCompositeAggParams;
    aggProfessionalItemsCount?: boolean;
    aggSupplierCount?: boolean;
    aggFacetOptions?: FacetOptionsAggregationParams[];
    aggDownloads?: SearchCompositeAggParams;
    aggAwards?: boolean;
}

export type CompositeAggKeys = Extract<
    keyof SearchAggregationParams,
    | 'aggProfessionals'
    | 'aggProductMaterials'
    | 'aggProfessions'
    | 'aggRegions'
    | 'aggLibraryRanges'
    | 'aggAssociations'
    | 'aggBrands'
    | 'aggDesigners'
    | 'aggProfessionalCategories'
    | 'aggDownloads'
>;

interface ColorOption {
    name: string;
    color: string;
}

export type BusinessFocusType =
    | 'Unspecified'
    | 'Mixed'
    | 'Residential'
    | 'Commercial';

export interface BusinessFocusOption {
    type: BusinessFocusType;
    docCount: number;
}

/**
 * A geographic point in the Search Service's `geo_point` request form.
 */
export interface SearchCoordinate {
    lat: number;
    lon: number;
}

/**
 * The schema for the `searchLocation` filter of the Search Service request body.
 */
export interface SearchLocation {
    suburb?: string;
    district?: string;
    city?: string;
    /**
     * GeoNames admin2 name. Ranking only — never filters and never belongs in
     * a location path URL.
     */
    division?: string;
    /**
     * City names that share the searched place's admin2 parent. Ranking only —
     * never filters and never belongs in a location path URL. Physical
     * premises store a city name, not the county, so this is how a Costa Mesa
     * page can prefer Orange over Napa.
     */
    divisionCities?: string[];
    region?: string;
    state?: string;
    country?: string;
    slug?: string;
    /**
     * The place's coordinates. Feeds the Search Service's distance ranking and
     * never filters the result set. Filter-panel URLs carry `latitude` /
     * `longitude` query params that `getLocation` reads into this field;
     * `location-{slug}` pages set it from the resolved ServiceableArea.
     */
    coordinate?: SearchCoordinate;
}

interface Profession {
    URLSegment: string;
    Title: string;
}

interface SearchListItem {
    label: string;
    value: string;
}

interface FacetOption {
    id: string;
    name: string;
}

interface FacetOptionsAggregationResult {
    name: string;
    isGeneral: boolean;
    items: FacetOption[];
    selectedItems?: FacetOption[];
}

export type FacetAggregationResult = Record<
    string,
    FacetOptionsAggregationResult
>;

interface Range {
    min: number;
    max: number;
}

/**
 * The response schema for the Search Service `aggregate` endpoint.
 */
export interface SearchAggregations {
    categoryTree?: {
        articles?: SearchCategory;
        products?: SearchCategory;
        professionals?: SearchCategory;
        projects?: SearchCategory;
    };
    productItemsCounts?: {
        purchasableCount?: number;
        onSaleCount?: number;
        inStockCount?: number;
        deliveryAvailableCount?: number;
        sampleAvailableCount?: number;
    };
    totalCount?: number;

    /** Distinct supplier (professional) count for the queried products. */
    supplierCount?: number;

    priceRange?: Range;

    productColors?: ColorOption[];
    productMaterials?: {
        after?: Record<string, unknown>;
        items: string[];
        selectedItems?: string[];
    };

    lengthRange?: Range;
    widthRange?: Range;
    heightRange?: Range;
    diameterRange?: Range;
    weightRange?: Range;

    professionals?: {
        after?: Record<string, unknown>;
        items: SearchProfessional[];
        selectedItems?: SearchProfessional[];
    };

    projectItemsCounts?: {
        hasProductItemCount?: number;
        hasVideoItemCount?: number;
        hasAwardItemCount?: number;
        projectOfTheMonthCount?: number;
    };

    professions?: {
        after?: Record<string, unknown>;
        items: Profession[];
        selectedItems?: Profession[];
    };

    projectCountRanges?: CountRange[];
    libraryRanges?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };
    businessFocuses?: BusinessFocusOption[];
    associations?: {
        after?: Record<string, unknown>;
        items: SearchAssociation[];
        selectedItems?: SearchAssociation[];
    };

    professionalItemsCounts?: {
        hasAwardItemCount?: number;
    };

    designers?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };

    brands?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };

    facetOptions?: FacetAggregationResult;

    professionalCategories?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };

    downloads?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };
    awards?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };
}

export interface FacetOptionsFilterParams {
    facet: string;
    option: string;
}

/**
 * The schema for the filters property of the Search Service request body.
 */
export interface SearchServiceFilters {
    categoryLink?: string;
    country?: 'NZ' | 'AU' | 'US' | 'all';
    professionals?: string[];

    // Product filters.
    isPurchasable?: boolean;
    isOnSale?: boolean;
    priceRange?: [number, number];
    isInStock?: boolean;
    isDeliveryAvailable?: boolean;
    isSampleAvailable?: boolean;
    colors?: string[];
    materials?: string[];
    lengthRange?: [number, number];
    widthRange?: [number, number];
    heightRange?: [number, number];
    diameterRange?: [number, number];
    weightRange?: [number, number];
    brands?: string[];
    designers?: string[];
    libraryRanges?: string[];
    downloads?: string[];
    facetOptions?: FacetOptionsFilterParams[];

    // Project filters.
    hasProducts?: boolean;
    hasVideos?: boolean;
    projectOfTheMonth?: boolean;
    professions?: string[];
    professionalsUsed?: number[];
    professionalCategories?: string[];
    /**
     * The mode to filter the professional's projects by (all, own projects, or
     * projects we're tagged in). Professional project page only.
     */
    viewProjectsBy?: SearchServiceViewProjectsByOption;

    // Professional filters.
    projectCount?: number;
    professionalParents?: number[];
    businessFocuses?: BusinessFocusType[];
    associations?: string[];
    excludeProductSuppliers?: boolean;
    architectsFirst?: boolean;

    hasAwards?: boolean;
    awards?: string[];
    /**
     * Filter projects by the IDs of professionals used in the project.
     * Professional project page only.
     */
    searchLocation?: SearchLocation;

    // Admin filters.
    noCategory?: boolean;
    invalidLink?: boolean;
    disabled?: boolean;
    profileOnly?: boolean;
}

type FilterKeys = keyof SearchServiceFilters;

export type CheckboxFilterKeys = Extract<
    FilterKeys,
    | 'isPurchasable'
    | 'isOnSale'
    | 'isInStock'
    | 'isDeliveryAvailable'
    | 'isSampleAvailable'
    | 'hasProducts'
    | 'hasVideos'
    | 'hasAwards'
    | 'projectOfTheMonth'
    | 'invalidLink'
    | 'disabled'
    | 'profileOnly'
>;

export type CheckboxGroupFilterKeys = Extract<FilterKeys, 'colors' | 'awards'>;

export type RangeFilterKeys = Extract<
    FilterKeys,
    | 'priceRange'
    | 'lengthRange'
    | 'widthRange'
    | 'heightRange'
    | 'diameterRange'
    | 'weightRange'
>;

export type SearchListFilterKeys = Extract<
    FilterKeys,
    | 'professionals'
    | 'materials'
    | 'libraryRanges'
    | 'associations'
    | 'professions'
    | 'brands'
    | 'designers'
    | 'professionalCategories'
    | 'downloads'
    | 'awards'
>;

export interface SearchCategory {
    docCount: number;
    title: string;
    url: string;
    child: Record<string, SearchCategory>;
}

interface SearchProfessional {
    Title: string;
    URLSegment: string;
}

type SearchAssociation = SearchProfessional;

export interface CountRange {
    from: number;
    count: number;
}

/**
 * The response schema for the Search Service `search` endpoint.
 */
export interface SearchResults<T> {
    hitsPerPage: number;
    nbHits: number;
    nbPages: number;
    page: number;
    maxResultWindow: number;
    hits: T[];
}

/**
 * Filters shared by the searchImage and searchImageAggregate endpoints.
 */
export interface SearchImageFilters {
    professionalLink?: string;
    roomTypePrimary?: string | string[];
    roomTypeSecondary?: string | string[];
    architecturalStyle?: string | string[];
    interiorStyle?: string | string[];
    material?: string | string[]; // Note: API uses 'material' (singular), not 'materials'
    colorPalette?: string | string[];
    [key: string]: unknown;
}

/**
 * Request parameters for the searchImage endpoint.
 */
export interface SearchImageRequest {
    search?: string;
    filters?: SearchImageFilters & { adminScore?: number };
    page?: number;
    from?: number;
    offset?: number;
    size?: number;
    limit?: number;
    sortBy?: string;
    visibilityType?: VisibilityType;
    /**
     * Return one row per photograph rather than one per label, by collapsing
     * on the index's `CollapseKey`. The Search Service serves it only from
     * imagelabel v7 onwards and drops it silently otherwise, so a caller reads
     * `collapsed` on the response to learn whether it was applied.
     */
    collapseByFile?: boolean;
}

/**
 * Aggregation parameters for the searchImageAggregate endpoint.
 */
export interface SearchImageAggregationParams {
    size?: number;
    search?: string;
    order?: 'asc' | 'desc';
}

/**
 * Request parameters for the searchImageAggregate endpoint.
 */
export interface SearchImageAggregateRequest {
    search?: string;
    filters?: SearchImageFilters;
    from?: number;
    offset?: number;
    size?: number;
    limit?: number;
    visibilityType?: VisibilityType;
    aggregations?: {
        aggRoomTypePrimary?: SearchImageAggregationParams;
        aggRoomTypeSecondary?: SearchImageAggregationParams;
        aggArchitecturalStyle?: SearchImageAggregationParams;
        aggInteriorStyle?: SearchImageAggregationParams;
        aggMaterialCategory?: SearchImageAggregationParams;
        aggMaterial?: SearchImageAggregationParams;
        aggMaterialFinish?: SearchImageAggregationParams;
        aggMaterialColourFamily?: SearchImageAggregationParams;
        aggColorPalettePrimary?: SearchImageAggregationParams;
        aggColorPaletteAccent?: SearchImageAggregationParams;
        aggColorPaletteTone?: SearchImageAggregationParams;
        aggColorPaletteContrast?: SearchImageAggregationParams;
        aggColorPaletteBrightness?: SearchImageAggregationParams;
        aggRoomSize?: SearchImageAggregationParams;
        aggCeilingHeight?: SearchImageAggregationParams;
        aggScaleCues?: SearchImageAggregationParams;
        aggProfessionals?: SearchImageAggregationParams;
        aggTotalCount?: boolean;
        /**
         * Count the distinct photographs behind each option rather than the
         * label rows, and add `totalImageCount` beside `totalCount`. A
         * modifier, not an aggregation: it runs no query of its own.
         *
         * A Search Service that predates it strips the key at its schema and
         * answers exactly as before, so the caller must fall back to `count`
         * rather than assume the field is there.
         */
        aggImageCount?: boolean;
        [key: string]: SearchImageAggregationParams | boolean | undefined;
    };
}
